namespace Solovis.Reports.DataSetHelpers {


    export interface IntervalStatsInspectionLineItemStats {
        assetStats: any;
        intervalStats: any;
        performanceVariationCode?: string;
        twrHolder: any;
        irrHolder: any;
        externalTxHolder: any;
        intervalFromDayId: number;
        intervalToDayId: number;
    };


    export class IntervalStatsInspectionHelper {

        /**
         * Give parameters for Interval Stats Line Item Inspection get parameters to use when creating lineItemStats data set.
         */
        public static getLineItemStatsParametersFromInspectionReportParameters(parameters) {
            var assetModes = {
                Asset: "Asset",
                Composite: "Composite"
            };

            var params: any = {};

            if (parameters.assetMode === assetModes.Asset) {
                var assetResourceIdsSeen = {};
                var assetResourceIds = [];

                if (parameters.asset) {
                    assetResourceIds.push(parameters.asset.resourceId);
                }

                params.assetResourceIds = assetResourceIds;
            }
            else if (parameters.assetMode === assetModes.Composite) {
                var compositeSelectionSet = parameters.compositeSelectionSet;
                var lineItemFullLabelPath = parameters.lineItemFullLabelPath;
                if (!compositeSelectionSet || !lineItemFullLabelPath) {
                    return null;
                }
                params.compositeSelectionSetRef = {
                    id: compositeSelectionSet.id,
                    custom: compositeSelectionSet.custom
                };

                params.outputFilter = {
                    fullLabelPaths: [lineItemFullLabelPath]
                };
            }
            else {
                return null;
            }

            params.statSetOptions = {     
                basicStats: {
                    cumulativeReturn: true,
                    annualizedReturn: true,
                    cumulativeIrr: true,
                    annualizedIrr: true
                },
                values: {
                    endValue: true,
                    startValue: true,
                    localValues: true
                }
            };

            if (parameters.performanceVariationCode) {
                params.statSetOptions.basicStats.twrVariations = [parameters.performanceVariationCode];
                params.statSetOptions.basicStats.irrVariations = [parameters.performanceVariationCode];
            }

            params.captureExternalTransactions = true;
            params.captureExternalTransactionsInLocalCurrency = true;
            params.captureIntramonthTwrPeriodSplitData = true;
            params.captureMonthlyReturns = true;
            params.capturePerformanceVariationsInfo = true;

            var to = parameters.to ? parameters.to : InvData.DayId.today();
            var from = parameters.from;

            params.intervalRequests = [
                { code: "Interval", fromDayId: from, toDayId: to }
            ];
        
            if (parameters.targetCurrency) {
                params.targetCurrencyId = parameters.targetCurrency.id;
            }

            params.sourceDataRef = parameters.sourceData;

            return params;
        }

        /**
         * Post-processing provided by lineItemStats data source on Line Item Inspection report.
         */
        public static doLineItemStatsPostProcess(loadedDataSet, more) {
            if (loadedDataSet.statsByAsset) {
                // TODO: Confirm expected asset and interval.
                let assetStats = loadedDataSet.statsByAsset[0];
                let intervalStats = assetStats.byInterval[0];

                let performanceVariationCode = more.originalParameters.performanceVariationCode;                

                let twrHolder = IntervalStatsInspectionHelper.findTwrHolder(intervalStats, performanceVariationCode);             
                let irrHolder = IntervalStatsInspectionHelper.findIrrHolder(intervalStats, performanceVariationCode);          
                let externalTxHolder = IntervalStatsInspectionHelper.findExternalTxHolder(assetStats, performanceVariationCode);

                let intervalFromDayId = null;
                let intervalToDayId = null;
                if (loadedDataSet.intervalRequests) {
                    const currentInterval = loadedDataSet.intervalRequests.find(r => r.code == intervalStats.intervalCode);
                    if (currentInterval) {
                        intervalFromDayId = currentInterval.fromDayId;
                        intervalToDayId = currentInterval.toDayId;
                    }
                }

                let dataSet = {
                    assetStats: assetStats,
                    intervalStats: intervalStats,
                    performanceVariationCode: performanceVariationCode,
                    twrHolder: twrHolder,
                    irrHolder: irrHolder,
                    externalTxHolder: externalTxHolder,
                    intervalFromDayId: intervalFromDayId,
                    intervalToDayId: intervalToDayId
                };

                return dataSet;
            }
            else {
                return {};
            }
        }   

        /**
         * Given a cellContext from "Interval Statistics" js report cell, we want to create the line item inspection parameters
         * to use.
         * @param cellContext
         */
        public static getLineItemInspectionInfoForIntervalStatsCellContext(cellContext) {
            let assetModes = {
                Assets: "Assets",
                Composite: "Composite"
            };

            let isGroupLineItem = function (lineItem) {
                if (lineItem.compositeInfo && lineItem.compositeInfo.isGroup) {
                    return true;
                }

                return false;
            };

            let isSyntheticComponentLineItem = function (lineItem) {
                if (lineItem.compositeInfo && lineItem.compositeInfo.isSyntheticComponent) {
                    return true;
                }

                return false;
            };

            let row = cellContext.row;
            let rowToUse = row;
            if (row._backingRow) {
                rowToUse = row._backingRow;
            }

            let col = cellContext.col;
            let tableControl = cellContext.dataTable;
            let parameters = tableControl.parameters;

            let items = [];
            let assetLinks;

            let getAssetInfoLinkFromResourceId = (resourceId) => {
                return Solovis.Reports.ReportLinker.getReportLink({
                    reportAlias: "AssetInfo",
                    parameters: {
                        entityOrInvestment: { resourceId: resourceId }
                    },
                    autoRun: true,
                    collapseConfigSection: true
                })
            };

            if (!isGroupLineItem(row)) {
                let assetRef: any = {};

                if (isSyntheticComponentLineItem(row)) {
                    assetRef.resourceId = Solovis.resolveObject("compositeInfo.underlyingComponentMetadata.resourceId", row);
                }
                else {
                    assetRef.resourceId = Solovis.resolveObject("assetInfo.resourceId", row);
                }

                if (assetRef.resourceId) {
                    assetLinks = Solovis.Reports.ReportLinker.getAssetLinks(assetRef);
                    items = items.concat(assetLinks);
                }
                
                if (row.assetInfo && row.assetInfo.investmentId) {
                    // Add context menu items for holding and owner.
                    if (row.assetInfo.holdingResourceId) {
                        items.push({
                            label: "Holding Info",
                            href: getAssetInfoLinkFromResourceId(row.assetInfo.holdingResourceId)
                        });
                    }
                    if (row.assetInfo.ownerResourceId) {
                        items.push({
                            label: "Owner Info",
                            href: getAssetInfoLinkFromResourceId(row.assetInfo.ownerResourceId)
                        });
                    }
                }                
            }            

            // See if we should add benchmark links.
            let benchmarkRefList = [
                { title: "Basic Stats", field: "basicStatsBenchmark" },
                { title: "Beta Market", field: "betaAlphaMarket" },
                { title: "Sortino Ratio Target Return", field: "sortinoRatioTargetReturn" },
                { title: "Sharpe Ratio", field: "sharpeRatioBenchmark" },
                { title: "Information Ratio", field: "informationRatioBenchmark" },
                { title: "Upside/Downside Capture Ratio", field: "upsideDownsideCaptureRatioBenchmark" },
                { title: "PME", field: "pmeBenchmark" }
            ];

            let hasAtLeastOneBmRef = false;
            for (let bmRef of benchmarkRefList) {
                if (rowToUse[bmRef.field]) {
                    hasAtLeastOneBmRef = true;
                    break;
                }                
            }

            if (hasAtLeastOneBmRef) {
                let benchmarkItems = [];

                let addBenchmarkResourceLink = (title, entry) => {
                    if (entry && entry.resourceId) {
                        benchmarkItems.push({
                            label: title + ": " + entry.label,
                            href: getAssetInfoLinkFromResourceId(entry.resourceId)
                        });
                    }
                };

                for (let bmRef of benchmarkRefList) {
                    addBenchmarkResourceLink(bmRef.title, rowToUse[bmRef.field]);
                }

                if (!_.isEmpty(benchmarkItems)) {
                    items.push({ label: "Benchmarks", links: benchmarkItems });
                }
            }

            // Inspection
            let reportCodeForLineItemInspection = "59249BC2-B2DD-45C5-85EC-13D4F1646C63";
            let inspectionParameters: any = {};
            let canBeInspected = false;

            if (parameters.assetMode === assetModes.Assets) {                
                inspectionParameters.assetMode = "Asset";

                if (rowToUse.assetInfo && rowToUse.assetInfo.resourceId) {
                    inspectionParameters.asset = { resourceId: rowToUse.assetInfo.resourceId };
                    canBeInspected = true;
                }
            }
            else if (parameters.assetMode === assetModes.Composite) {
                inspectionParameters.assetMode = "Composite";

                if (rowToUse.compositeInfo && rowToUse.compositeInfo.fullLabelPath) {
                    inspectionParameters.lineItemFullLabelPath = rowToUse.compositeInfo.fullLabelPath;
                    canBeInspected = true;
                }

                inspectionParameters.compositeSelectionSet = parameters.compositeSelectionSet;
            }

            inspectionParameters.targetCurrency = parameters.targetCurrency;
            inspectionParameters.sourceData = parameters.sourceData;

            let intervalLabel = "";

            if (!parameters.showIntervalsAsColumns) {
                inspectionParameters.from = rowToUse.intervalFromDayId;
                inspectionParameters.to = rowToUse.intervalToDayId;
            }
            else {
                if (col && col.custom && col.custom.intervalCode) {
                    // Narrow down time range to column set.
                    let intervalStats = tableControl.dataSets.intervalStats;
                    let intervalInfo = intervalStats.intervalSetResult.codeToIntervalInfoMap[col.custom.intervalCode];
                    intervalLabel = intervalInfo.label;
                    inspectionParameters.from = intervalInfo.intervalRequest.fromDayId;
                    inspectionParameters.to = intervalInfo.intervalRequest.toDayId;
                }
                else {
                    inspectionParameters.from = rowToUse.minIntervalFromDayId;
                    inspectionParameters.to = rowToUse.maxIntervalToDayId;
                }
            }

            if (col && col.custom && col.custom.performanceVariationCode) {
                inspectionParameters.performanceVariationCode = col.custom.performanceVariationCode;
            }

            if (canBeInspected) {
                let inspectionUrl = Solovis.Reports.ReportLinker.getReportLink({
                    reportCode: reportCodeForLineItemInspection,
                    parameters: inspectionParameters,
                    autoRun: true
                });

                if (items.length > 0) {
                    items.push({ "separator": true });
                }

                items.push({
                    label: "Inspect Line Item" + (intervalLabel ? (" (" + intervalLabel + ")") : ""),
                    href: inspectionUrl
                });

                if (col && col.custom) {
                  if ((
                      col.custom.statsField === "cumulativeReturn" ||
                      col.custom.statsField === "annualizedReturn" ||
                      col.custom.statsField === "annCumTwrReturn"
                    ) && !col.custom.forBenchmark) {
                    items.push({
                      label: "Download Excel TWR Worksheet",
                      click: () => {
                        IntervalStatsInspectionHelper.exportWorksheetFromIntervalStats(cellContext, { worksheet: "TWR" });
                      }
                    });
                  }

                  if ((
                      col.custom.statsField === "cumulativeIrr" || 
                      col.custom.statsField === "annualizedIrr" ||
                      col.custom.statsField === "annCumIrr"
                    ) && !col.custom.forBenchmark) {
                    items.push({
                      label: "Download Excel IRR Worksheet",
                      click: () => {
                        IntervalStatsInspectionHelper.exportWorksheetFromIntervalStats(cellContext, { worksheet: "IRR" });
                      }
                    });
                  }
                }
            }

            let inspectionInfo = {
                inspectionParameters: inspectionParameters,
                intervalLabel: intervalLabel,
                assetLinks: assetLinks,
                cellContextMenuItems: items
            };

            return inspectionInfo;
        }


        private static addSummaryWorksheet(excelWorkbook: any, lineItemStats: IntervalStatsInspectionLineItemStats) {
            let ws = excelWorkbook.addWorksheet('Summary');

            let assetStats = lineItemStats.assetStats;
            let intervalStats = lineItemStats.intervalStats;
            let assetInfo = assetStats ? assetStats.assetInfo : null;
            let compositeInfo = assetStats ? assetStats.compositeInfo : null;

            ws.getColumn(1).width = 26;
            ws.getColumn(2).width = 30;

            let nextRow = 1;

            let row;

            if (assetInfo) {
                row = ws.getRow(nextRow);
                row.getCell(1).value = "Label";
                row.getCell(2).value = assetInfo.label;
                nextRow++;

                if (assetInfo.resourceId) {
                    row = ws.getRow(nextRow);
                    row.getCell(1).value = "Resource Id";
                    row.getCell(2).value = assetInfo.resourceId;
                    nextRow++;
                }

                if (assetInfo.investmentId) {
                    row = ws.getRow(nextRow);
                    row.getCell(1).value = "Investment Id";
                    row.getCell(2).value = assetInfo.investmentId;
                    nextRow++;
                }

                if (assetInfo.entityId) {
                    row = ws.getRow(nextRow);
                    row.getCell(1).value = "Entity Id";
                    row.getCell(2).value = assetInfo.entityId;
                    nextRow++;
                }

                if (assetInfo.currencyCode) {
                    row = ws.getRow(nextRow);
                    row.getCell(1).value = "Target Currency";
                    row.getCell(2).value = assetInfo.currencyCode;
                    nextRow++;
                }

                if (assetInfo.currencyId) {
                    row = ws.getRow(nextRow);
                    row.getCell(1).value = "Target Currency Id";
                    row.getCell(2).value = assetInfo.currencyId;
                    nextRow++;
                }

                if (assetInfo.localCurrencyCode) {
                    row = ws.getRow(nextRow);
                    row.getCell(1).value = "Local Currency";
                    row.getCell(2).value = assetInfo.localCurrencyCode;
                    nextRow++;
                }

                if (assetInfo.localCurrencyId) {
                    row = ws.getRow(nextRow);
                    row.getCell(1).value = "Local Currency Id";
                    row.getCell(2).value = assetInfo.localCurrencyId;
                    nextRow++;
                }         
            }
                      
            if (compositeInfo) {
                row = ws.getRow(nextRow);
                row.getCell(1).value = "Is Group";
                row.getCell(2).value = (compositeInfo.isGroup ? "Yes" : "No");
                nextRow++;

                row = ws.getRow(nextRow);
                row.getCell(1).value = "Full Label Path";
                row.getCell(2).value = compositeInfo.fullLabelPath;
                nextRow++;
            }

            if (intervalStats.twrDayCount) {
                row = ws.getRow(nextRow);
                row.getCell(1).value = "TWR Day Count";
                row.getCell(2).value = intervalStats.twrDayCount;
                nextRow++;
            }

            if (intervalStats.twrFromDayIdUsed) {
                row = ws.getRow(nextRow);
                row.getCell(1).value = "TWR From Day Id Used";
                row.getCell(2).value = intervalStats.twrFromDayIdUsed;
                nextRow++;

                row = ws.getRow(nextRow);
                row.getCell(1).value = "TWR From Date Used";
                Solovis.Reports.Util.ExcelJsHelper.setDayIdAsDateInExcelCell(row.getCell(2), intervalStats.twrFromDayIdUsed);
                nextRow++;
            }

            if (intervalStats.twrToDayIdUsed) {
                row = ws.getRow(nextRow);
                row.getCell(1).value = "TWR To Day Id Used";
                row.getCell(2).value = intervalStats.twrToDayIdUsed;
                nextRow++;

                row = ws.getRow(nextRow);
                row.getCell(1).value = "TWR To Date Used";
                Solovis.Reports.Util.ExcelJsHelper.setDayIdAsDateInExcelCell(row.getCell(2), intervalStats.twrToDayIdUsed);
                nextRow++;
            }

            if (intervalStats.txBasedFromDayIdUsed) {
                row = ws.getRow(nextRow);
                row.getCell(1).value = "TX-Based From Day Id Used";
                row.getCell(2).value = intervalStats.txBasedFromDayIdUsed;
                nextRow++;

                row = ws.getRow(nextRow);
                row.getCell(1).value = "TX-Based From Date Used";
                Solovis.Reports.Util.ExcelJsHelper.setDayIdAsDateInExcelCell(row.getCell(2), intervalStats.txBasedFromDayIdUsed);
                nextRow++;
            }

            if (intervalStats.txBasedToDayIdUsed) {
                row = ws.getRow(nextRow);
                row.getCell(1).value = "TX-Based To Day Id Used";
                row.getCell(2).value = intervalStats.txBasedToDayIdUsed;
                nextRow++;

                row = ws.getRow(nextRow);
                row.getCell(1).value = "TX-Based To Date Used";
                Solovis.Reports.Util.ExcelJsHelper.setDayIdAsDateInExcelCell(row.getCell(2), intervalStats.txBasedToDayIdUsed);
                nextRow++;
            }
        }

        private static addInspectionParametersWorksheet(excelWorkbook: any, inspectionParameters: any) {
            let ws = excelWorkbook.addWorksheet('Parameters');

            ws.getColumn(1).width = 26;

            let nextRow = 1;

            let row = ws.getRow(nextRow);
            row.getCell(1).value = "Asset Mode";
            row.getCell(2).value = inspectionParameters.assetMode;
            nextRow++;

            if (inspectionParameters.assetMode === "Asset") {
                row = ws.getRow(nextRow);
                row.getCell(1).value = "Asset";
                row.getCell(2).value = JSON.stringify(inspectionParameters.asset);
                nextRow++;
            }
            else if (inspectionParameters.assetMode === "Composite") {
                row = ws.getRow(nextRow);
                row.getCell(1).value = "Composite Selection Set";
                row.getCell(2).value = JSON.stringify(inspectionParameters.compositeSelectionSet);
                nextRow++;

                row = ws.getRow(nextRow);
                row.getCell(1).value = "Line Item Full Label Path";
                row.getCell(2).value = inspectionParameters.lineItemFullLabelPath;
                nextRow++;
            }

            row = ws.getRow(nextRow);
            row.getCell(1).value = "From";
            row.getCell(2).value = inspectionParameters.from;
            nextRow++;

            row = ws.getRow(nextRow);
            row.getCell(1).value = "To";
            row.getCell(2).value = inspectionParameters.to;
            nextRow++;

            row = ws.getRow(nextRow);
            row.getCell(1).value = "Source Data";
            row.getCell(2).value = JSON.stringify(inspectionParameters.sourceData);
            nextRow++;

            row = ws.getRow(nextRow);
            row.getCell(1).value = "Target Currency";
            row.getCell(2).value = JSON.stringify(inspectionParameters.targetCurrency);
            nextRow++;

            row = ws.getRow(nextRow);
            row.getCell(1).value = "Performance Variation Code";
            row.getCell(2).value = inspectionParameters.performanceVariationCode;
            nextRow++;
        }

        
        /**
         * Find object holding the cumulativeReturn and annualizedReturn fields.
         */
        public static findTwrHolder(intervalStats, performanceVariationCode) {
            if (intervalStats) {
                if (!performanceVariationCode) {
                    return intervalStats.basicStats;
                }
                else if (intervalStats.basicStats) {
                    let resultToUse = null;
                    $(intervalStats.basicStats.twrVariations).each(function (i, variationResult: any) {
                        if (variationResult.code === performanceVariationCode) {
                            resultToUse = variationResult;
                        }
                    });

                    return resultToUse;
                }                
            }
        }

       /**
         * Find object holding the cumulativeIrr and annualizedIrr fields.
         */
        public static findIrrHolder(intervalStats, performanceVariationCode) {
            if (intervalStats) {
                if (!performanceVariationCode) {
                    return intervalStats.basicStats;
                }
                else if (intervalStats.basicStats) {
                    let resultToUse = null;
                    $(intervalStats.basicStats.irrVariations).each(function (i, variationResult: any) {
                        if (variationResult.code === performanceVariationCode) {
                            resultToUse = variationResult;
                        }
                    });

                    return resultToUse;
                }                
            }
        }

       /**
         * Find object holding the externalTransactions and externalTransactionsInLocalCurrency fields.
         */
        public static findExternalTxHolder(assetStats, performanceVariationCode) {
            if (assetStats) {
                if (!performanceVariationCode) {
                    return assetStats;
                }
                else if (assetStats.externalTransactionsByVariation) {
                    let resultToUse = null;
                    $(assetStats.externalTransactionsByVariation).each(function (i, variationResult: any) {
                        if (variationResult.code === performanceVariationCode) {
                            resultToUse = variationResult;
                        }
                    });

                    return resultToUse;
                }                
            }
        }

        public static findMonthlyReturnsToUse(intervalStats, performanceVariationCode) {
            if (intervalStats && intervalStats.capturedMonthlyReturns) {
                let monthlyReturnsToUse = null;                

                if (!performanceVariationCode) {
                    monthlyReturnsToUse = intervalStats.capturedMonthlyReturns.monthlyReturns;
                }
                else {
                    $(intervalStats.capturedMonthlyReturns.byVariation).each(function (i, entry: any) {
                        if (entry.code === performanceVariationCode) {
                            monthlyReturnsToUse = entry.monthlyReturns;
                        }
                    });
                }

                return monthlyReturnsToUse;
            }
        }



        public static exportTwrWorksheet(lineItemStats: IntervalStatsInspectionLineItemStats, inspectionParameters?: any) {

            let performanceVariationCode = lineItemStats.performanceVariationCode;

            let assetStats = lineItemStats.assetStats;
            let intervalStats = lineItemStats.intervalStats;
            let basicStats = intervalStats.basicStats;
            let twrDayCount = intervalStats.twrDayCount;
        
            let twrHolder = lineItemStats.twrHolder ? lineItemStats.twrHolder : {};

            Solovis.ModuleLoader.modalEnsureModuleListLoaded(["exceljs"], function () {
                var ExcelJS = (window as any).ExcelJS;

                var wb = new ExcelJS.Workbook();
                
                var ws = wb.addWorksheet('TWR');

                var percentFormat = '0.00000%';

                ws.getCell("A1").value = "NOTE: 'Enable Editing' to allow formulas to be calculated.";

                ws.getCell('A3').value = "Cumulative Return";
                var overallAumulativeReturnCell = ws.getCell('B3');
                ws.getCell('B3').numFmt = percentFormat;

                ws.getCell('A4').value = "TWR Day Count";
                ws.getCell('B4').value = twrDayCount;

                ws.getCell('A5').value = "Annualized Return";
                ws.getCell('B5').value = { "formula": "POWER(1 + B3, 365 / B4) - 1" };
                ws.getCell('B5').numFmt = percentFormat;

                ws.getCell('A6').value = "Data Set Cumulative Return";
                ws.getCell('B6').value = twrHolder.cumulativeReturn;
                ws.getCell('B6').numFmt = percentFormat;     

                ws.getCell('A7').value = "Data Set Annualized Return";
                ws.getCell('B7').value = twrHolder.annualizedReturn;
                ws.getCell('B7').numFmt = percentFormat;
                           
                let nextRow = 8;

                var periodFromCol = "A";
                var periodToCol = "B";
                var periodReturnCol = "C";
                var sourceCol = "D";
                var onePlusRCol = "E";
                var cumProdOnePlusRCol = "F";
                var cumReturnCol = "G";                

                if (performanceVariationCode) {
                    ws.getRow(nextRow).getCell(1).value = "Performance Variation Code";
                    ws.getRow(nextRow).getCell(2).value = performanceVariationCode;
                    nextRow++;
                }

                // Skip row before rest of data.
                nextRow++;

                ws.getColumn(periodFromCol).width = 26;
                ws.getColumn(periodToCol).width = 15;
                ws.getColumn(periodReturnCol).width = 15;
                ws.getColumn(onePlusRCol).width = 15;
                ws.getColumn(cumProdOnePlusRCol).width = 15;
                ws.getColumn(cumReturnCol).width = 15;
                ws.getColumn(periodToCol).width = 15;

                var headerRow = ws.getRow(nextRow);
                headerRow.getCell(periodFromCol).value = 'From';
                headerRow.getCell(periodToCol).value = 'To';
                headerRow.getCell(periodReturnCol).value = 'Period Return';
                headerRow.getCell(sourceCol).value = 'Source';
                headerRow.getCell(onePlusRCol).value = '(1 + r)';
                headerRow.getCell(cumProdOnePlusRCol).value = 'Cum. prod of each (1 + r)';
                headerRow.getCell(cumReturnCol).value = 'Cum. Return';

                nextRow++;
                var firstValueRow = nextRow;

                let monthlyReturns = IntervalStatsInspectionHelper.findMonthlyReturnsToUse(intervalStats, performanceVariationCode);

                if (monthlyReturns) {                    
                    $(monthlyReturns).each(function (i, monthlyReturn: any) {
                        var row = ws.getRow(nextRow);

                        Solovis.Reports.Util.ExcelJsHelper.setDayIdAsDateInExcelCell(row.getCell(periodFromCol), monthlyReturn.fromDayId);
                        Solovis.Reports.Util.ExcelJsHelper.setDayIdAsDateInExcelCell(row.getCell(periodToCol), monthlyReturn.toDayId);

                        var periodReturnCell = row.getCell(periodReturnCol);
                        periodReturnCell.value = monthlyReturn["return"];
                        periodReturnCell.numFmt = percentFormat;

                        row.getCell(sourceCol).value = monthlyReturn.source;

                        row.getCell(onePlusRCol).value = { "formula": "1 + " + (periodReturnCol + nextRow) };

                        if (nextRow > firstValueRow) {
                            row.getCell(cumProdOnePlusRCol).value = { "formula": (onePlusRCol + nextRow) + " * " + (cumProdOnePlusRCol + (nextRow - 1)) };
                        }
                        else {
                            row.getCell(cumProdOnePlusRCol).value = { "formula": (onePlusRCol + nextRow) };
                        }

                        var cumReturnCell = row.getCell(cumReturnCol);
                        cumReturnCell.value = { "formula": (cumProdOnePlusRCol + nextRow) + " - 1" };
                        cumReturnCell.numFmt = percentFormat;

                        nextRow++;
                    });                    
                }

                var lastCumReturnCellRef = cumReturnCol + (nextRow - 1);
                overallAumulativeReturnCell.value = { "formula": lastCumReturnCellRef };

                IntervalStatsInspectionHelper.addSummaryWorksheet(wb, lineItemStats);
                if (inspectionParameters) {
                    IntervalStatsInspectionHelper.addInspectionParametersWorksheet(wb, inspectionParameters);
                }

                IntervalStatsInspectionHelper.saveXlsx(wb, "twr_perf.xlsx");
            });                
        }


        public static exportIrrWorksheet(lineItemStats: IntervalStatsInspectionLineItemStats, inspectionParameters?: any) {
            let performanceVariationCode = lineItemStats.performanceVariationCode;
            let irrHolder = lineItemStats.irrHolder ? lineItemStats.irrHolder : {};
            let externalTxHolder = lineItemStats.externalTxHolder ? lineItemStats.externalTxHolder : {};

            let isSinceInceptionInterval = Solovis.isNull(inspectionParameters.from);

            let assetStats = lineItemStats.assetStats;
            let intervalStats = lineItemStats.intervalStats;
            let basicStats = intervalStats.basicStats;
            let valuesResult = intervalStats.valuesResult;
            let intervalFromDayId = lineItemStats.intervalFromDayId;
            let intervalToDayId = lineItemStats.intervalToDayId;
            let txBasedFromDayIdUsed = intervalStats.txBasedFromDayIdUsed;
            let txBasedToDayIdUsed = intervalStats.txBasedToDayIdUsed;
            let dayCount = txBasedToDayIdUsed - txBasedFromDayIdUsed;

            let startValue = null;
            let endValue = null;

            // NOTE: We shouldn't include startValue if this a SinceInception interval. The first day's transactions will take care of the starting point.
            if (!isSinceInceptionInterval) {
                if (valuesResult && valuesResult.startValue && valuesResult.startValue.totalValue) {
                    startValue = valuesResult.startValue;
                }
            }

            if (valuesResult && valuesResult.endValue && valuesResult.endValue.totalValue) {
                endValue = valuesResult.endValue;
            }

            let txList = _.map(
                externalTxHolder.externalTransactions,
                function (tx: any) {
                    let entry: any = {};

                    if (tx.transactionEntryId) {
                        entry.label = "Tx #" + tx.transactionEntryId;
                    }
                    else {
                        entry.label = "Tx";
                    }

                    entry.dayId = tx.effectiveDayId;
                    entry.isStartOfDay = tx.isStartOfDay;
                    entry.perfDayId = tx.effectiveDayId + (tx.isStartOfDay ? -1 : 0);
                    entry.netAmountIn = (tx.amountIn ? tx.amountIn : 0) - (tx.amountOut ? tx.amountOut : 0);
                    return entry;
                });
            // Make sure we only look at transactions in current time window if we aren't doing a since inception interval.
            if (!isSinceInceptionInterval) {
                txList = _.filter(txList, function (entry: any) {
                    if (entry.dayId > intervalFromDayId && entry.dayId <= intervalToDayId) {
                        return true;
                    }
                });
            }

            Solovis.ModuleLoader.modalEnsureModuleListLoaded(["exceljs"], function () {
                var ExcelJS = (window as any).ExcelJS;

                var wb = new ExcelJS.Workbook();

                var ws = wb.addWorksheet('IRR');

                var percentFormat = '0.00000%';

                ws.getCell("A1").value = "NOTE: 'Enable Editing' to allow formulas to be calculated.";

                ws.getCell('A3').value = "Excel's XIRR";
                let xirrCell = ws.getCell('B3');
                xirrCell.numFmt = percentFormat;

                ws.getCell('A4').value = "Day Count";
                let dayCountCell = ws.getCell('B4');
                dayCountCell.value = dayCount;
                let dayCountCellRef = 'B4';

                ws.getCell('A5').value = "De-annualized XIRR";
                ws.getCell('B5').value = { "formula": "POWER(1 + B3, B4 / 365) - 1" };
                ws.getCell('B5').numFmt = percentFormat;

                ws.getCell('A6').value = "Data Set Day Count";
                ws.getCell('B6').value = dayCount;                

                ws.getCell('A7').value = "Data Set Annualized IRR";
                ws.getCell('B7').value = irrHolder.annualizedIrr;
                ws.getCell('B7').numFmt = percentFormat;

                ws.getCell('A8').value = "Data Set Cumulative IRR";
                ws.getCell('B8').value = irrHolder.cumulativeIrr;
                ws.getCell('B8').numFmt = percentFormat;
                let dataSetCumulativeIrrCellRef = 'B8';

                ws.getCell('A9').value = "NPV Component Sums";                                
                let npvComponentSumsCell = ws.getCell('B9');
                npvComponentSumsCell.numFmt = '0.00000';

                let nextRow = 10;

                if (performanceVariationCode) {
                    ws.getRow(nextRow).getCell(1).value = "Performance Variation Code";
                    ws.getRow(nextRow).getCell(2).value = performanceVariationCode;
                    nextRow++;
                }

                let labelCol = 'A';
                let dateCol = 'B';
                let perfDateCol = 'C';
                let netAmountInCol = 'D';
                let daysSinceStartCol = 'E';
                let npvComponentCol = 'F';

                ws.getColumn(labelCol).width = 24;
                ws.getColumn(dateCol).width = 15;
                ws.getColumn(perfDateCol).width = 15;
                ws.getColumn(netAmountInCol).width = 20;
                ws.getColumn(daysSinceStartCol).width = 20;
                ws.getColumn(npvComponentCol).width = 20;

                // Add extra line break.
                nextRow++;

                var headerRow = ws.getRow(nextRow);
                headerRow.getCell(labelCol).value = 'Label';
                headerRow.getCell(dateCol).value = 'Date';
                headerRow.getCell(perfDateCol).value = 'Perf Date';
                headerRow.getCell(netAmountInCol).value = 'Net Amount In';
                headerRow.getCell(daysSinceStartCol).value = 'Days Since Start';
                headerRow.getCell(npvComponentCol).value = 'NPV Component';

                nextRow++;

                let firstFlowRow = nextRow;
                let firstPerfDateCellRef = perfDateCol + firstFlowRow;

                let row = null;

                let setNpvFormulaCols = (row, rowNum) => {
                    row.getCell(daysSinceStartCol).value = { "formula": "ROUND(" + (perfDateCol + rowNum) + " - " + firstPerfDateCellRef + ", 0)" };

                    let netAmountInCellRef = netAmountInCol + rowNum;
                    let dayWeightFormula = "(" + (daysSinceStartCol + rowNum) + " / " + dayCountCellRef + ")";
                    row.getCell(npvComponentCol).value = { "formula": netAmountInCellRef + " / POWER(1 + " + dataSetCumulativeIrrCellRef + ", " + dayWeightFormula + ")" };
                };

                if (startValue) {
                    row = ws.getRow(nextRow);
                    row.getCell(labelCol).value = "Start Value";
                    Solovis.Reports.Util.ExcelJsHelper.setDayIdAsDateInExcelCell(row.getCell(dateCol), startValue.dayId);
                    Solovis.Reports.Util.ExcelJsHelper.setDayIdAsDateInExcelCell(row.getCell(perfDateCol), startValue.dayId);
                    row.getCell(netAmountInCol).value = startValue.totalValue;
                    setNpvFormulaCols(row, nextRow);
                    nextRow++;
                }                

                $(txList).each(function (i, entry: any) {
                    row = ws.getRow(nextRow);
                    row.getCell(labelCol).value = entry.label;
                    Solovis.Reports.Util.ExcelJsHelper.setDayIdAsDateInExcelCell(row.getCell(dateCol), entry.dayId);
                    Solovis.Reports.Util.ExcelJsHelper.setDayIdAsDateInExcelCell(row.getCell(perfDateCol), entry.perfDayId);
                    row.getCell(netAmountInCol).value = entry.netAmountIn;
                    setNpvFormulaCols(row, nextRow);
                    nextRow++;
                });

                if (endValue) {
                    row = ws.getRow(nextRow);
                    row.getCell(labelCol).value = "End Value";
                    Solovis.Reports.Util.ExcelJsHelper.setDayIdAsDateInExcelCell(row.getCell(dateCol), endValue.dayId);
                    Solovis.Reports.Util.ExcelJsHelper.setDayIdAsDateInExcelCell(row.getCell(perfDateCol), endValue.dayId);
                    row.getCell(netAmountInCol).value = -endValue.totalValue;
                    setNpvFormulaCols(row, nextRow);
                    nextRow++;
                }

                let lastFlowRow = nextRow - 1;

                if (lastFlowRow >= firstFlowRow) {
                    let valueCellRefs = (netAmountInCol + firstFlowRow) + ":" + (netAmountInCol + lastFlowRow);
                    let dateCellRefs = (perfDateCol + firstFlowRow) + ":" + (perfDateCol + lastFlowRow);
                    xirrCell.value = { "formula": "XIRR(" + valueCellRefs + ", " + dateCellRefs + ")" };

                    let npvComponentRefs = (npvComponentCol + firstFlowRow) + ":" + (npvComponentCol + lastFlowRow);
                    npvComponentSumsCell.value = { "formula": "SUM(" + npvComponentRefs + ")" };

                    dayCountCell.value = { "formula": (daysSinceStartCol + lastFlowRow) };
                }

                IntervalStatsInspectionHelper.addSummaryWorksheet(wb, lineItemStats);
                if (inspectionParameters) {
                    IntervalStatsInspectionHelper.addInspectionParametersWorksheet(wb, inspectionParameters);
                }

                IntervalStatsInspectionHelper.saveXlsx(wb, "irr_perf.xlsx");
            });
        }



        private static saveXlsx(wb: any, downloadFilename: string) {
            wb.xlsx.writeBuffer({
                base64: true
            })
                .then(function (xls64) {
                    // NOTE: Tested in Chrome and IE11.                    
                    var data = new Blob([xls64], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });

                    (window as any).saveAs(data, downloadFilename, { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });                    
                })
                .catch(function (error) {
                    console.log(error.message);
                });
        }


        public static async exportWorksheetFromIntervalStats(intervalStatsCellContext, worksheetOptions) {
            let inspectionInfo = IntervalStatsInspectionHelper.getLineItemInspectionInfoForIntervalStatsCellContext(intervalStatsCellContext);

            let inspectionParameters = inspectionInfo.inspectionParameters;

            let lineItemStatsParams = IntervalStatsInspectionHelper.getLineItemStatsParametersFromInspectionReportParameters(inspectionParameters);

            let response = await Solovis.Reports.ReportDataLoader.loadDataSetAsync(
                "/Solovis/Performance/IntervalStats",
                lineItemStatsParams,
                null,
                { invokeMethod: "invokeAndWait" });

            let result = response.result;
            let dataSet = result.dataSet;

            let postProcessMore = {
                originalParameters: inspectionParameters
            };

            let lineItemStats = IntervalStatsInspectionHelper.doLineItemStatsPostProcess(dataSet, postProcessMore) as IntervalStatsInspectionLineItemStats;

            if (worksheetOptions.worksheet === "TWR") {
                IntervalStatsInspectionHelper.exportTwrWorksheet(lineItemStats, inspectionParameters);
            }
            else if (worksheetOptions.worksheet === "IRR") {
                IntervalStatsInspectionHelper.exportIrrWorksheet(lineItemStats, inspectionParameters);
            }
        }
    }

}